import React, { useState } from "react";
import api, { errMsg } from "../../core/api";
import { useApp } from "../../core/AppContext";
import FormFields from "../../components/FormFields";
import RecordPage from "../../components/RecordPage";
import { formatDate, todayIso } from "../../core/format";
import MultiFileField from "../../components/MultiFileField";
import { NewsModal } from "../../components/AnnouncementFiles";

function ChangePassword() {
  const { toast } = useApp();
  const [form, setForm] = useState({ currentPassword: "", newPassword: "", repeat: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const fields = [
    { name: "currentPassword", label: "رمز فعلی", type: "password", full: true },
    { name: "newPassword", label: "رمز جدید (حداقل ۶ کاراکتر)", type: "password" },
    { name: "repeat", label: "تکرار رمز جدید", type: "password" },
  ];
  const save = async () => {
    setErr("");
    if (form.newPassword.length < 6) return setErr("رمز جدید باید حداقل ۶ کاراکتر باشد");
    if (form.newPassword !== form.repeat) return setErr("تکرار رمز با رمز جدید یکسان نیست");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { currentPassword: form.currentPassword, newPassword: form.newPassword });
      toast("رمز عبور تغییر کرد");
      setForm({ currentPassword: "", newPassword: "", repeat: "" });
    } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="card card-pad" style={{ maxWidth: 620 }}>
      <h3 style={{ marginBottom: 12, color: "var(--primary)" }}>تغییر رمز عبور</h3>
      {err && <div className="alert err">{err}</div>}
      <FormFields fields={fields} form={form} setForm={setForm} />
      <button className="btn" onClick={save} disabled={busy}>{busy ? "در حال ذخیره..." : "تغییر رمز"}</button>
      <div className="hint" style={{ marginTop: 8 }}>پس از تغییر رمز، سایر دستگاه‌های وارد‌شده خارج می‌شوند.</div>
    </div>
  );
}

function Users({ employees }) {
  const { meta, user } = useApp();
  const roles = Object.entries(meta?.roles || {}).map(([value, label]) => ({ value, label }));
  const empOpts = [{ value: "", label: "— بدون ارتباط —" }, ...employees.map((e) => ({ value: e.id, label: `${e.firstName} ${e.lastName}` }))];
  return (
    <RecordPage title="کاربران سامانه" subtitle="ایجاد حساب ورود برای کارکنان و مدیر بندر" endpoint="/users" createLabel="افزودن کاربر"
      canCreate canEdit canDelete initial={{ role: "staff", isActive: true }}
      toForm={(r) => ({ ...r, employeeId: r.employeeId ?? "", password: "" })}
      columns={[
        { key: "username", label: "نام کاربری", render: (r) => <b dir="ltr">{r.username}</b> },
        { key: "role", label: "نقش", render: (r) => meta?.roles?.[r.role] },
        { key: "employeeName", label: "کارمند مرتبط" },
        { key: "isActive", label: "وضعیت", render: (r) => <span className={`badge ${r.isActive ? "ok" : "bad"}`}>{r.isActive ? "فعال" : "غیرفعال"}</span> },
        { key: "lastLogin", label: "آخرین ورود", render: (r) => (r.lastLogin ? formatDate(r.lastLogin.slice(0, 10)) : "—") },
      ]}
      getFields={(form) => [
        { name: "username", label: "نام کاربری (انگلیسی)", required: true, ltr: true, disabled: !!form.id },
        { name: "password", label: form.id ? "رمز جدید (اختیاری)" : "رمز عبور", type: "password", required: !form.id, hint: "حداقل ۶ کاراکتر" },
        { name: "role", label: "نقش", type: "select", options: roles, required: true },
        { name: "employeeId", label: "کارمند مرتبط", type: "select", options: empOpts, hint: "برای نقش «کارمند» الزامی است" },
        { name: "isActive", label: "وضعیت", type: "select", hidden: !form.id, options: [{ value: true, label: "فعال" }, { value: false, label: "غیرفعال" }] },
      ]}
      rowExtra={() => null}
      editFilter={() => true}
      key={user.id} />
  );
}
function News() {
  const [filesFor, setFilesFor] = useState(null);
  const [tick, setTick] = useState(0);
  return (
    <>
      <RecordPage key={tick} title="اخبار و اطلاعیه‌ها" subtitle="در داشبورد برای همه‌ی کاربران نمایش داده می‌شود" endpoint="/announcements" createLabel="افزودن اطلاعیه"
        canCreate canEdit canDelete initial={{ publishDate: todayIso() }}
        afterSave={async (data, form, { editing, row }) => {
          const files = form.attachments || [];
          if (!files.length) return;
          const id = editing ? row.id : data?.item?.id ?? data?.id;
          if (!id) throw new Error("no id");
          const fd = new FormData();
          files.forEach((f) => fd.append("files", f));
          await api.post(`/announcements/${id}/files`, fd, { timeout: 180000 });
        }}
        rowExtra={(r) => (
          <button className="icon-btn" title="مشاهده و مدیریت فایل‌ها" onClick={() => setFilesFor(r)}>
            <i className="fa-solid fa-paperclip" />
          </button>
        )}
        columns={[
          { key: "publishDate", label: "تاریخ", render: (r) => formatDate(r.publishDate) },
          { key: "title", label: "عنوان", render: (r) => <b>{r.title}</b> },
          { key: "body", label: "متن", render: (r) => <div className="clip">{r.body || "—"}</div> },
        ]}
        fields={[
          { name: "title", label: "عنوان", required: true, full: true },
          { name: "publishDate", label: "تاریخ انتشار", type: "date", required: true, full: true },
          { name: "body", label: "متن", type: "textarea" },
          {
            name: "attachments", label: "فایل‌های پیوست", type: "custom", noBody: true, full: true,
            render: ({ form, setForm, errors }) => (
              <MultiFileField label="فایل‌ها / تصاویر پیوست (اختیاری، یک یا چند فایل)"
                files={form.attachments || []} error={errors.attachments}
                hint="در ویرایش، فایل‌های جدید به فایل‌های قبلی اضافه می‌شوند. برای حذف فایل‌های قبلی از دکمه‌ی گیره استفاده کنید."
                onChange={(v) => setForm((s) => ({ ...s, attachments: v }))} />
            ),
          },
        ]} />
      {filesFor && <NewsModal id={filesFor.id} admin onClose={() => setFilesFor(null)} onChanged={() => {}} />}
    </>
  );
}

export default function Settings() {
  const { user } = useApp();
  const admin = user.role === "admin";
  const [tab, setTab] = useState("password");
  const [employees, setEmployees] = useState([]);
  const pick = (t) => {
    setTab(t);
    if (t === "users" && !employees.length) api.get("/employees").then((r) => setEmployees(r.data.items)).catch(() => {});
  };
  return (
    <>
      {admin && (
        <div className="tabs">
          <button className={`tab-btn ${tab === "password" ? "on" : ""}`} onClick={() => pick("password")}>رمز عبور من</button>
          <button className={`tab-btn ${tab === "users" ? "on" : ""}`} onClick={() => pick("users")}>کاربران</button>
          <button className={`tab-btn ${tab === "news" ? "on" : ""}`} onClick={() => pick("news")}>اخبار و اطلاعیه‌ها</button>
        </div>
      )}
      {tab === "password" && <>{!admin && <div className="page-head"><h2>تنظیمات</h2></div>}<ChangePassword /></>}
      {admin && tab === "users" && <Users employees={employees} />}
      {admin && tab === "news" && <News />}
    </>
  );
}
